import React from 'react'
import styled, { css } from 'styled-components'
import { Grid, H, Image, P, Wrapper } from '@farewill/ui'
import { BREAKPOINT, COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { screenMin, screenMax } from '@farewill/ui/helpers/responsive'
import { PRODUCTS, FUNERAL } from 'lib/products/constants'
import Cta from 'components/Cta'
import { CTA_TYPES } from 'components/Cta/constants'
import AspectRatioWrapper from 'components/AspectRatioWrapper'
import BreakpointSwitch from 'components/BreakpointSwitch'

const StyledWrapper = styled.div<{ $background: string }>`
  background: ${({ $background }) => $background};
  padding-top: 100px;
  padding-bottom: 100px;

  ${screenMax.l`
    padding-top: 40px;
    padding-bottom: 50px;
  `}
`

const StyledFigure = styled.figure`
  margin: 0;

  img {
    height: 100%;
    object-fit: cover;
  }

  ${screenMin.l`
    margin: 0 ${GTR.L};
  `}

  ${screenMax.l`
    img {
      object-position: top;
    }
  `}
`

const StyledTextWrapper = styled(Wrapper)<{ $descriptionSize: string }>`
  ${({ $descriptionSize }) =>
    $descriptionSize === 'M' &&
    css`
      ${screenMin.l`
        padding-top: ${GTR.L};
      `}

      ${screenMin.xl`
        padding-top: ${GTR.XXL};
      `}
    `}
`

const StyledHDesktopOnly = styled(H)`
  display: none;

  ${screenMin.m`
    display: block;
    margin-bottom: ${GTR.L};
  `}
`

const StyledHMobileOnly = styled(H)`
  display: block;

  ${screenMin.m`
    display: none;
  `}
`

const StyledP = styled(P)<{
  size: string
}>`
  ${({ size }) =>
    size === 'L' &&
    css`
      ${screenMax.l`
          font-size: ${FONT.SIZE.M};
        `}
    `}

  ${({ size }) =>
    size === 'M' &&
    css`
      ${screenMax.l`
          font-size: ${FONT.SIZE.M};
        `}
    `}
`

const StyledFigcaption = styled.figcaption`
  font-size: ${FONT.SIZE.S};
  color: ${COLOR.BLACK};
  margin-top: ${GTR.S};

  ${screenMin.l`
    margin-top: ${GTR.M};
  `}
`

interface Props {
  caption?: string | null
  description: string | string[]
  descriptionSize?: 'M' | 'L'
  image:
    | string
    | {
        file: {
          url: string
        }
      }
  phoneNumber?: string | null
  title: string
  background?: string
}

const FuneralDirectors = ({
  caption = null,
  description,
  descriptionSize = 'L',
  image,
  phoneNumber = null,
  title,
  background = '#fff9dc',
}: Props): React.ReactElement => {
  const descriptionArray = Array.isArray(description)
    ? description
    : [description]

  return (
    <StyledWrapper $background={background}>
      <Grid container containerPaddingTop="0" containerPaddingBottom="0">
        <Grid.Item
          startColumnFromS={1}
          spanFromS={12}
          startColumnFromM={1}
          spanFromM={6}
        >
          <StyledHMobileOnly tag="h2" size="M" decorative>
            {title}
          </StyledHMobileOnly>
          <StyledFigure>
            <BreakpointSwitch
              aboveComponent={
                <AspectRatioWrapper ratio={522 / 494}>
                  {typeof image === 'string' ? (
                    <Image path={image} width={600} stretch />
                  ) : (
                    <Image
                      path={image?.file.url}
                      formatPath={({ path, width }) => `${path}?w=${width}`}
                      width={600}
                      stretch
                    />
                  )}
                </AspectRatioWrapper>
              }
              belowComponent={
                <AspectRatioWrapper ratio={16 / 9}>
                  {typeof image === 'string' ? (
                    <Image path={image} width={600} widthFromS={900} stretch />
                  ) : (
                    <Image
                      path={image.file.url}
                      formatPath={({ path, width }) => `${path}?w=${width}`}
                      width={600}
                      widthFromS={900}
                      stretch
                    />
                  )}
                </AspectRatioWrapper>
              }
              breakAt={BREAKPOINT.S}
            />
            {caption && <StyledFigcaption>{caption}</StyledFigcaption>}
          </StyledFigure>
        </Grid.Item>
        <Grid.Item
          startColumnFromS={1}
          spanFromS={12}
          startColumnFromM={7}
          spanFromM={6}
        >
          <StyledTextWrapper $descriptionSize={descriptionSize}>
            <StyledHDesktopOnly tag="h2" size="M" decorative>
              {title}
            </StyledHDesktopOnly>
            {descriptionArray.map((descItem, index) => {
              if (index === descriptionArray.length - 1 && phoneNumber)
                return (
                  <StyledP
                    key={descItem.slice(0, 3).toLowerCase().replace(' ', '')}
                    size={descriptionSize}
                    color={COLOR.BLACK}
                    margin={[0, 0, 'XL']}
                  >
                    {descItem}
                  </StyledP>
                )

              return (
                <StyledP
                  key={descItem.slice(0, 3).toLowerCase().replace(' ', '')}
                  size={descriptionSize}
                  color={COLOR.BLACK}
                >
                  {descItem}
                </StyledP>
              )
            })}
            {phoneNumber && (
              <Cta
                type={CTA_TYPES.TELEPHONE_NUMBER}
                text="Call us now"
                telephoneNumber={phoneNumber}
                useSwappedNumber={false}
                product={PRODUCTS[FUNERAL]}
              />
            )}
          </StyledTextWrapper>
        </Grid.Item>
      </Grid>
    </StyledWrapper>
  )
}

export default FuneralDirectors
